import { LitElement, html } from 'lit';

/**
 * Quote Line Item Component
 * Displays a single billing line item with description, quantity, rate, and total
 * Responsive layout that adapts to screen size
 */
class QuoteLineItem extends LitElement {
    static properties = {
        line: { type: Object },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.line = null;
    }

    get description() {
        return this.line?.dtypDesc || 'N/A';
    }

    get quantity() {
        return this.line?.qty || 'N/A';
    }

    get rate() {
        return this.line?.rate || 'N/A';
    }

    get per() {
        return this.line?.per || '';
    }

    get total() {
        return this.line?.total || 'N/A';
    }

    get notes() {
        return this.line?.notes || null;
    }

    get fullDescription() {
        return this.description;
    }

    get rateDisplay() {
        const parts = [this.rate];
        if (this.per) {
            parts.push(this.per);
        }
        return parts.join(' ');
    }

    render() {
        if (!this.line) {
            return html``;
        }

        return html`
            <div class="border-b border-base-300 last:border-b-0">
                <!-- Mobile: Stacked layout -->
                <div class="block md:hidden p-3 space-y-2">
                    <div class="flex justify-between items-start">
                        <div class="flex-1">
                            <p class="font-medium text-gray-900">${this.fullDescription}</p>
                            ${this.notes
                                ? html` <p class="text-xs text-gray-500 mt-1">${this.notes}</p> `
                                : ''}
                        </div>
                        <p class="font-semibold text-gray-900 ml-4">${this.total}</p>
                    </div>
                    <div
                        class="grid grid-cols-2 gap-2 text-sm text-gray-600 pt-2 border-t border-base-200"
                    >
                        <div>
                            <span class="text-xs text-gray-500">Qty:</span>
                            ${this.quantity}
                        </div>
                        <div>
                            <span class="text-xs text-gray-500">Rate:</span>
                            ${this.rateDisplay}
                        </div>
                    </div>
                </div>

                <!-- Desktop: Table row layout -->
                <div
                    class="hidden md:grid md:grid-cols-12 gap-4 p-3 items-center hover:bg-base-200/50 transition-colors"
                >
                    <!-- Description (40%) -->
                    <div class="col-span-5">
                        <p class="font-medium text-gray-900">${this.fullDescription}</p>
                        ${this.notes
                            ? html` <p class="text-xs text-gray-500 mt-1">${this.notes}</p> `
                            : ''}
                    </div>

                    <!-- Quantity (15%) -->
                    <div class="col-span-2 text-right">
                        <p class="text-gray-700">${this.quantity}</p>
                    </div>

                    <!-- Rate (25%) -->
                    <div class="col-span-3 text-right">
                        <p class="text-gray-700">${this.rateDisplay}</p>
                    </div>

                    <!-- Total (20%) -->
                    <div class="col-span-2 text-right">
                        <p class="font-semibold text-gray-900">${this.total}</p>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('quote-line-item', QuoteLineItem);
